import { useContext, useEffect, useMemo, useState } from "react";
import { Add, Refresh, SearchNormal1, TicketDiscount } from "iconsax-react";
import { useTranslation } from "react-i18next";
import DBButton from "../../../component/global/DBButton";
import DBConfirmModal from "../../../component/global/DBConfirmModal";
import PaginationComponent from "../../../component/global/PaginationComponent";
import { del, get } from "../../../controlers/global/ConfigRequest";
import {
    DELETE_DISCOUNT_CARD,
    DISCOUNT_CARD,
    SALE_CARD_DATA,
} from "../../../helperData/ApiAddress";
import { GlobalStateContext } from "../../../layouts/GlobalStates";
import OfferDetailModal from "./OfferDetailModal";
import OfferEditor from "./OfferEditor";
import OfferTicket from "./OfferTicket";
import { matchesOfferFilter, remainingLabel } from "./helpers";
import "./style.scss";

const PAGE_SIZE = 20;

export default function OffersManagement({ activeTab }) {
    const { t } = useTranslation();
    const { auth } = useContext(GlobalStateContext);
    const [offers, setOffers] = useState([]);
    const [pages, setPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [scope, setScope] = useState("all");
    const [carts, setCarts] = useState([]);
    const [editor, setEditor] = useState({ open: false, id: null });
    const [detail, setDetail] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);

    const canManage = Boolean(
        auth?.company_admin && auth?.package?.discount_permission
    );

    const load = (pageIndex = currentPage) => {
        setLoading(true);
        setError("");
        get(DISCOUNT_CARD, { page: pageIndex + 1 })
            .then((response) => {
                setOffers(response.results || []);
                setPages(Math.max(1, Math.ceil((response.count || 0) / PAGE_SIZE)));
            })
            .catch(() => setError(t("offers.loadError")))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(currentPage);
        get(SALE_CARD_DATA)
            .then((response) => setCarts(response || []))
            .catch(() => setCarts([]));
    }, [activeTab, currentPage, refreshToken]);

    const visible = useMemo(
        () =>
            offers.filter((offer) =>
                matchesOfferFilter(offer, { query, scope })
            ),
        [offers, query, scope]
    );

    const stats = useMemo(() => {
        const remaining = offers.reduce((sum, offer) => {
            return sum + remainingLabel(offer.remain_count, offer.discount_cards_count).remain;
        }, 0);
        const avg =
            offers.length === 0
                ? 0
                : Math.round(
                      offers.reduce(
                          (sum, offer) => sum + (Number(offer.discount_amount) || 0),
                          0
                      ) / offers.length
                  );
        return { count: offers.length, remaining, avg };
    }, [offers]);

    const confirmDelete = () => {
        if (!deleteTarget) {
            return;
        }
        setDeleting(true);
        del(DELETE_DISCOUNT_CARD.replace("{id}/", `${deleteTarget.id}/`))
            .finally(() => {
                setDeleting(false);
                setDeleteTarget(null);
                setRefreshToken((value) => value + 1);
            });
    };

    return (
        <div className="offers-board">
            <header className="offers-board__header">
                <div>
                    <h1>{t("offers.title")}</h1>
                    <span>{t("offers.subtitle")}</span>
                </div>
                {canManage && (
                    <DBButton
                        type="primary"
                        caption={
                            <>
                                <Add size={18} aria-hidden="true" />
                                {t("offers.create")}
                            </>
                        }
                        onClick={() => setEditor({ open: true, id: null })}
                    />
                )}
            </header>

            <section className="offers-stats" aria-label={t("offers.stats")}>
                <div>
                    <span>{t("offers.stat.active")}</span>
                    <strong className="is-tabular">{stats.count}</strong>
                </div>
                <div>
                    <span>{t("offers.stat.remaining")}</span>
                    <strong className="is-tabular">{stats.remaining}</strong>
                </div>
                <div>
                    <span>{t("offers.stat.avg")}</span>
                    <strong className="is-tabular">{stats.avg}%</strong>
                </div>
            </section>

            <div className="offers-toolbar">
                <label className="offer-field offer-field--search">
                    <span>{t("offers.search.label")}</span>
                    <span className="offer-field__control">
                        <SearchNormal1 size={18} aria-hidden="true" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={t("offers.search.placeholder")}
                        />
                    </span>
                </label>
                <div
                    className="offer-scope"
                    role="radiogroup"
                    aria-label={t("offers.filter.field")}
                >
                    {["all", "itemType", "item"].map((value) => (
                        <label key={value} className={scope === value ? "is-active" : ""}>
                            <input
                                type="radio"
                                name="offer-scope"
                                value={value}
                                checked={scope === value}
                                onChange={() => setScope(value)}
                            />
                            {t(`offers.filter.${value}`)}
                        </label>
                    ))}
                </div>
            </div>

            {loading && (
                <div className="offer-state" role="status" aria-live="polite">
                    <span className="offer-spinner" aria-hidden="true" />
                    {t("offers.loading")}
                </div>
            )}
            {!loading && error && (
                <div className="offer-state offer-state--error" role="alert">
                    <p>{error}</p>
                    <button type="button" onClick={() => load(currentPage)}>
                        <Refresh size={16} aria-hidden="true" />
                        {t("offers.retry")}
                    </button>
                </div>
            )}
            {!loading && !error && visible.length === 0 && (
                <div className="offer-empty">
                    <TicketDiscount size={36} aria-hidden="true" />
                    <h2>{t("offers.empty.title")}</h2>
                    <p>{t("offers.empty.body")}</p>
                    {canManage && (
                        <DBButton
                            type="primary"
                            caption={t("offers.create")}
                            onClick={() => setEditor({ open: true, id: null })}
                        />
                    )}
                </div>
            )}
            {!loading && !error && visible.length > 0 && (
                <div className="offers-grid">
                    {visible.map((offer) => (
                        <OfferTicket
                            key={offer.id}
                            data={offer}
                            onOpen={() => setDetail(offer)}
                            onEdit={() => setEditor({ open: true, id: offer.id })}
                            onDelete={() => setDeleteTarget(offer)}
                        />
                    ))}
                </div>
            )}

            {!loading && pages > 1 && (
                <PaginationComponent
                    pageCount={pages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    fetchData={({ pageIndex }) => setCurrentPage(pageIndex)}
                    feedbacks={offers}
                />
            )}

            <OfferEditor
                show={editor.open}
                editId={editor.id}
                carts={carts}
                onHide={() => setEditor({ open: false, id: null })}
                onSaved={() => setRefreshToken((value) => value + 1)}
            />
            <OfferDetailModal
                show={Boolean(detail)}
                data={detail || {}}
                onHide={() => setDetail(null)}
            />
            <DBConfirmModal
                show={Boolean(deleteTarget)}
                onHide={() => setDeleteTarget(null)}
                title={t("offers.delete.title")}
                description={t("offers.delete.body", {
                    name: deleteTarget?.name || "",
                })}
                btnTitle={t("offers.delete")}
                onConfirm={confirmDelete}
                loading={deleting}
            />
        </div>
    );
}
